import { Helmet } from 'react-helmet-async';
import { Route, Routes, useLocation } from 'react-router';

import '../initDayjs';

import { observer } from 'mobx-react-lite';
import { lazy, Suspense, useEffect } from 'react';
import { usePageViewTracking } from './hooks/useAnalytics';
import { useButtonClickGuard } from './hooks/useButtonClickGuard';
import { useFcmToken } from './hooks/useFcmToken';
import ListeningDashboardPage from './listeningViews/dashboard/ListeningDashboardPage';
import LessonIntroPage from './listeningViews/lesson/LessonIntroPage';
import LessonPage from './listeningViews/lesson/LessonPage';
import ListeningHomePage from './listeningViews/ListeningHomePage';
import ListeningLayout from './listeningViews/ListeningLayout';
import ListeningPublicLayout from './listeningViews/ListeningPublicLayout';
import ListeningSettingsPage from './listeningViews/settings/ListeningSettingsPage';
import ListeningTermsLayout from './listeningViews/terms/ListeningTermsLayout';
import ListeningTermsPage from './listeningViews/terms/ListeningTermsPage';
import UiPage from './listeningViews/UiPage';
import deviceStore from './stores/deviceStore';
import firebaseStore from './stores/firebaseStore';
import historyStore from './stores/historyStore';
import themeStore from './stores/themeStore';
import webViewStore from './stores/webViewStore';

// 어드민 페이지는 별도 청크로 분리하여 일반 사용자가 로드하지 않도록 함
const AdminLayout = lazy(() => import('./listeningViews/admin/AdminLayout'));

const App = observer(() => {
    const location = useLocation();

    useEffect(() => {
        historyStore.push(location);
    }, [location.pathname]);

    useEffect(() => {
        deviceStore.init();
        webViewStore.initialize();
        themeStore.initialize();

        // 순서 중요: webViewStore.initialize() 가 먼저 호출되어야 함
        firebaseStore.initialize(webViewStore.isWebView);
    }, []);

    usePageViewTracking();
    useFcmToken();

    useButtonClickGuard();

    return (
        // @see React Router: https://reactrouter.com/start/library/routing
        <>
            <Helmet>
                <title>더리스닝</title>
            </Helmet>
            <Routes>
                {/* 공개 페이지 (NavBar 없음) */}
                <Route element={<ListeningPublicLayout />}>
                    <Route index element={<ListeningHomePage />} />
                    <Route element={<ListeningTermsLayout />}>
                        <Route path="/terms" element={<ListeningTermsPage type="terms" />} />
                        <Route path="/privacy" element={<ListeningTermsPage type="privacy" />} />
                        <Route path="/license" element={<ListeningTermsPage type="license" />} />
                    </Route>
                </Route>
                {/* 로그인 사용자 페이지 (NavBar 포함) */}
                <Route element={<ListeningLayout />}>
                    <Route path="/dashboard" element={<ListeningDashboardPage />} />
                    <Route path="/settings" element={<ListeningSettingsPage />} />
                    <Route path="/my/lesson/:lessonHash" element={<LessonPage />} />
                    <Route path="/lesson/:lessonHash" element={<LessonIntroPage />} />
                </Route>
                {import.meta.env.DEV && <Route path="/ui" element={<UiPage />} />}
                {/* 어드민 페이지 (별도 청크로 분리) */}
                <Route
                    path="/admin/*"
                    element={
                        <Suspense fallback={null}>
                            <AdminLayout />
                        </Suspense>
                    }
                />
            </Routes>
        </>
    );
});

export default App;
